import React, { useMemo, useState } from 'react';
import Button from './Button';
import {
  AddToSalesforceBusiness,
  AddToSalesforceOtherLocation,
  AddToSalesforcePayload,
  SF_ACCOUNT_TYPE_OPTIONS,
  SF_SUBSECTOR_OPTIONS,
  SF_TIER_OPTIONS,
  SfAccountType,
  normalizeBaseUrl,
  normalizeAccountName,
  formatAENotesPrefill,
  buildFranchiseAeNotes,
} from './AddToSalesforceModal';
import './AddToSalesforceModal.css';

export interface BulkAddBusiness extends AddToSalesforceBusiness {
  // Stable identifier for selection state across renders. Lead Finder uses
  // `${name}|${address}` as its hover key; reuse that here for parity.
  key: string;
  // Firmographic data sourced from Waterfall's apollo_data.employee_count when
  // the row was pre-enriched (via the bulk Enrich+Add button). Forwarded into
  // each per-row payload so the backend can write NumberOfEmployees on create.
  employee_count?: number;
  // Sibling locations when this row was selected as the primary of a multi-
  // location franchise card. Empty / undefined for single-location rows.
  // Mirrors what the single-add modal does: the AE Notes prefill lists each
  // other branch so the SF Account captures the full franchise footprint.
  other_locations?: AddToSalesforceOtherLocation[];
}

interface RowState {
  include: boolean;
  type: SfAccountType;
  // Empty string = inherit the bulk default. A specific SF_SUBSECTOR_OPTIONS
  // value = explicit row override. Industry is REQUIRED before submit — there
  // is no server-classifier fallback in bulk (Apollo is off; reps must choose).
  industry: string;
  // Empty = inherit bulk Tier default (which itself may be empty = unset).
  // Account.Tier__c is optional in SF, so unset is a valid final value.
  tier: string;
  aeNotes: string;
}

interface Props {
  businesses: BulkAddBusiness[];
  onClose: () => void;
  // Parent fires the bulk POST and renders the toast — the modal closes
  // optimistically as soon as the rep confirms.
  onSubmit: (rows: AddToSalesforcePayload[]) => void;
}

const BulkAddToSalesforceModal: React.FC<Props> = ({ businesses, onClose, onSubmit }) => {
  const [bulkType, setBulkType] = useState<SfAccountType>('New');
  // Empty = no bulk default chosen yet. Industry is required, so the rep must
  // either pick a bulk default here or override every included row.
  const [bulkIndustry, setBulkIndustry] = useState<string>('');
  // Empty = no bulk Tier default (rows can still set their own). Tier is OPTIONAL.
  const [bulkTier, setBulkTier] = useState<string>('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const initial: Record<string, RowState> = {};
    for (const b of businesses) {
      initial[b.key] = {
        include: true,
        type: 'New',
        industry: '',
        tier: '',
        // Use the franchise-aware builder so multi-location selections list
        // their sibling branches in AE Notes (the single-add modal does the
        // same). Falls back to the plain prefill when other_locations is empty.
        aeNotes: buildFranchiseAeNotes(
          b.rating, b.user_ratings_total, b.other_locations,
          b.last_review_age_days, b.business_status,
        ),
      };
    }
    return initial;
  });

  const setRow = (key: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const includedCount = useMemo(
    () => Object.values(rows).filter(r => r.include).length,
    [rows],
  );

  // Each row's effective industry: per-row override wins, else the bulk default.
  const resolvedIndustry = (r: RowState): string => r.industry || bulkIndustry;

  // Rows that are included but have no industry resolved — these block submit.
  const missingIndustryCount = useMemo(
    () => businesses.filter(b => {
      const r = rows[b.key];
      return r && r.include && !resolvedIndustry(r);
    }).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, bulkIndustry, businesses],
  );

  const canSubmit = includedCount > 0 && missingIndustryCount === 0;

  // "Apply to all" pushes the current bulk defaults into every per-row state.
  // The rep can still override individual rows afterward. Without this button,
  // the only way to mass-set Type/Industry is to expand every row.
  const applyDefaults = () => {
    setRows(prev => {
      const next: Record<string, RowState> = {};
      for (const [k, r] of Object.entries(prev)) {
        next[k] = { ...r, type: bulkType, industry: bulkIndustry, tier: bulkTier };
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    const payloads: AddToSalesforcePayload[] = [];
    for (const b of businesses) {
      const r = rows[b.key];
      if (!r || !r.include) continue;
      const industry = resolvedIndustry(r);
      if (!industry) continue;  // guarded by canSubmit, belt-and-suspenders

      // Tier precedence: per-row override > bulk default > unset.
      const tier = r.tier || bulkTier || undefined;
      payloads.push({
        name: normalizeAccountName(b.name.trim()),
        website: b.website ? normalizeBaseUrl(b.website) : undefined,
        phone: b.phone,
        type: r.type,
        industry,
        tier,
        billing_latitude: b.location?.lat,
        billing_longitude: b.location?.lng,
        // Forward the raw formatted address — backend parses it into
        // BillingStreet/City/State/PostalCode (and mirrors Billing →
        // Shipping). Without this, bulk-added accounts landed with empty
        // postal addresses even when Lead Finder displayed one on the card.
        address: b.address || undefined,
        ae_notes: r.aeNotes.trim() || undefined,
        company_linkedin: b.linkedin_url || undefined,
        company_instagram: b.instagram_url || undefined,
        company_facebook: b.facebook_url || undefined,
        company_x: b.x_url || undefined,
        company_yelp: b.yelp_url || undefined,
        company_email: b.company_email || undefined,
        // Forward the Waterfall headcount when the row was pre-enriched. The
        // backend bulk create reads this and writes Account.NumberOfEmployees.
        employee_count: b.employee_count,
      });
    }
    onSubmit(payloads);
    onClose();
  };

  return (
    <div className="add-sf-modal-backdrop" onClick={onClose}>
      <div className="add-sf-modal add-sf-modal-bulk" onClick={(e) => e.stopPropagation()}>
        <header className="add-sf-modal-header">
          <h2>Add {includedCount} {includedCount === 1 ? 'Account' : 'Accounts'} to Salesforce</h2>
          <button type="button" className="add-sf-modal-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="add-sf-modal-body">
          <section className="bulk-defaults">
            <h3>Defaults (apply to all)</h3>
            <div className="bulk-defaults-row">
              <label className="add-sf-field">
                <span>Type</span>
                <select
                  className="add-sf-select"
                  value={bulkType}
                  onChange={(e) => setBulkType(e.target.value as SfAccountType)}
                >
                  {SF_ACCOUNT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label className="add-sf-field">
                <span>Industry <span className="add-sf-required">*</span></span>
                <select
                  className="add-sf-select"
                  value={bulkIndustry}
                  onChange={(e) => setBulkIndustry(e.target.value)}
                >
                  <option value="">— Select industry —</option>
                  {SF_SUBSECTOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="add-sf-field">
                <span>Tier</span>
                <select className="add-sf-select" value={bulkTier}
                        onChange={(e) => setBulkTier(e.target.value)}>
                  <option value="">— None —</option>
                  {SF_TIER_OPTIONS.map(t => <option key={t} value={t}>Tier {t}</option>)}
                </select>
              </label>
              <Button type="button" variant="secondary" size="sm" onClick={applyDefaults}>
                Apply to all rows
              </Button>
            </div>
          </section>

          <section className="bulk-rows">
            <h3>Per-account ({includedCount} of {businesses.length} included)</h3>
            <div className="bulk-rows-list">
              {businesses.map((b) => {
                const r = rows[b.key];
                if (!r) return null;
                const isExpanded = expandedKey === b.key;
                const effectiveIndustry = resolvedIndustry(r);
                const industryMissing = r.include && !effectiveIndustry;
                return (
                  <div key={b.key} className={`bulk-row${r.include ? '' : ' is-excluded'}${industryMissing ? ' is-invalid' : ''}`}>
                    <label className="bulk-row-check">
                      <input
                        type="checkbox"
                        checked={r.include}
                        onChange={(e) => setRow(b.key, { include: e.target.checked })}
                      />
                    </label>
                    <div className="bulk-row-main">
                      <div className="bulk-row-name">
                        <strong>{normalizeAccountName(b.name)}</strong>
                        {b.website && <span className="bulk-row-site"> · {normalizeBaseUrl(b.website)}</span>}
                      </div>
                      <div className="bulk-row-meta">
                        <span>Type: <code>{r.type}</code></span>
                        <span> · Industry: {effectiveIndustry
                          ? <code>{effectiveIndustry}</code>
                          : <span className="bulk-row-warn">choose industry</span>}
                        </span>
                        <button
                          type="button"
                          className="bulk-row-toggle"
                          onClick={() => setExpandedKey(isExpanded ? null : b.key)}
                        >
                          {isExpanded ? 'collapse' : 'override'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="bulk-row-override">
                          <label className="add-sf-field">
                            <span>Type</span>
                            <select
                              className="add-sf-select"
                              value={r.type}
                              onChange={(e) => setRow(b.key, { type: e.target.value as SfAccountType })}
                            >
                              {SF_ACCOUNT_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </label>
                          <label className="add-sf-field">
                            <span>Industry</span>
                            <select
                              className="add-sf-select"
                              value={r.industry}
                              onChange={(e) => setRow(b.key, { industry: e.target.value })}
                            >
                              <option value="">{bulkIndustry ? `Inherit default (${bulkIndustry})` : '— Select industry —'}</option>
                              {SF_SUBSECTOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </label>
                          <label className="add-sf-field">
                            <span>Tier</span>
                            <select
                              className="add-sf-select"
                              value={r.tier}
                              onChange={(e) => setRow(b.key, { tier: e.target.value })}
                            >
                              <option value="">{bulkTier ? `Inherit default (Tier ${bulkTier})` : '— None —'}</option>
                              {SF_TIER_OPTIONS.map(t => <option key={t} value={t}>Tier {t}</option>)}
                            </select>
                          </label>
                          <label className="add-sf-field">
                            <span>AE Notes</span>
                            <textarea
                              rows={2}
                              value={r.aeNotes}
                              onChange={(e) => setRow(b.key, { aeNotes: e.target.value })}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <p className="bulk-prefill-hint">
            AE Notes auto-fills per row from Google review data, e.g.{' '}
            <code>{formatAENotesPrefill(4.7, 123)}</code>. Click "override" on
            any row to edit before submit.
          </p>

          {missingIndustryCount > 0 && (
            <p className="bulk-industry-warning">
              {missingIndustryCount} {missingIndustryCount === 1 ? 'row needs' : 'rows need'} an
              industry. Pick a default above (then "Apply to all rows") or set it per row.
            </p>
          )}
        </div>

        <footer className="add-sf-modal-footer">
          <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {includedCount === 0
              ? 'Nothing selected'
              : missingIndustryCount > 0
                ? `${missingIndustryCount} need industry`
                : `Confirm: Add ${includedCount}`}
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default BulkAddToSalesforceModal;
